/* import __COLOCATED_TEMPLATE__ from './add-content.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { EntityType } from 'embercom/models/data/entity-types';
import type ConversationExtractionService from 'embercom/services/conversation-extraction-service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import type CopilotApi from 'embercom/services/copilot-api';
import { StandalonePlatform } from 'embercom/objects/standalone/constants';
import type AddWebsiteModalService from 'embercom/services/add-website-modal-service';

interface Args {
  showModal: (syncSourceType: string, entityType: EntityType) => Promise<void>;
}

export default class AddContent extends Component<Args> {
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare appService: AppService;
  @service declare intercomEventService: any;
  @service declare conversationExtractionService: ConversationExtractionService;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare copilotApi: CopilotApi;
  @service declare addWebsiteModalService: AddWebsiteModalService;

  @tracked openSectionIdForCopilotOnlyAccordion: string | null = null;

  get conversationExtractionEnabled() {
    return this.conversationExtractionSettings?.enabled;
  }

  get conversationExtractionSettings() {
    return this.conversationExtractionService.conversationExtractionSettings;
  }

  @action
  createArticle() {
    this.knowledgeHubService.createContent(EntityType.ArticleContent, {}, false, false);
    this.trackAnalyticsEvent('create', 'public-article');
  }

  @action
  syncWebsite() {
    this.addWebsiteModalService.openAddWebsiteSheet();
    this.trackAnalyticsEvent('create', 'website');
  }

  @action
  createSnippet() {
    this.knowledgeHubService.createContent(EntityType.ContentSnippet, {}, false, false);
    this.trackAnalyticsEvent('create', 'snippet');
  }

  @action
  async uploadPdf() {
    await this.args.showModal('file-upload', EntityType.FileSourceContent);
    this.trackAnalyticsEvent('create', 'pdf');
  }

  @action
  async syncZendesk() {
    await this.args.showModal('zendesk-sync', EntityType.ArticleContent);
    this.trackAnalyticsEvent('create', 'zendesk-article');
  }

  @action
  async syncSalesforce() {
    this.router.transitionTo(this.router.currentRouteName, {
      queryParams: {
        openSalesforceSync: true,
      },
    });
    this.trackAnalyticsEvent('create', 'salesforce-knowledge');
  }

  get showFreshdeskSync() {
    return (
      this.appService.app.hasStandalonePlatform(StandalonePlatform.Freshdesk) ||
      !this.appService.app.canUseStandalone
    );
  }

  @action
  async syncFreshdesk() {
    await this.args.showModal('freshdesk-sync', EntityType.ExternalContent);
    this.trackAnalyticsEvent('create', 'freshdesk-knowledge');
  }

  @action
  async useConversationExtraction() {
    // awaiting here to check permissions
    await this.args.showModal('conversation-extraction', EntityType.ContentSnippet);
    this.trackAnalyticsEvent('create', 'conversation-extraction');
  }

  @action
  async syncNotion() {
    await this.args.showModal('notion-sync', EntityType.InternalArticle);
    this.trackAnalyticsEvent('create', 'notion-knowledge');
  }

  @action
  async syncConfluence() {
    await this.args.showModal('confluence-sync', EntityType.InternalArticle);
    this.trackAnalyticsEvent('create', 'confluence-knowledge');
  }

  @action
  async syncGuru() {
    await this.args.showModal('guru-sync', EntityType.InternalArticle);
    this.trackAnalyticsEvent('create', 'guru-knowledge');
  }

  @action
  createInternalArticle() {
    this.knowledgeHubService.createContent(EntityType.InternalArticle, {}, false, false);
    this.trackAnalyticsEvent('create', 'internal-article');
  }

  get internalArticleContentSources() {
    return [
      {
        icon: 'locked',
        description: this.intl.t('fin-ai-agent.knowledge.add-content.internal-article'),
        contentSource: 'internal-article',
        onClick: this.createInternalArticle,
      },
      {
        icon: 'confluence',
        description: this.intl.t('fin-ai-agent.knowledge.add-content.sync-confluence'),
        contentSource: 'sync-confluence',
        onClick: this.syncConfluence,
      },
      {
        icon: 'notion',
        description: this.intl.t('fin-ai-agent.knowledge.add-content.sync-notion'),
        contentSource: 'sync-notion',
        onClick: this.syncNotion,
      },
      {
        icon: 'guru',
        description: this.intl.t('fin-ai-agent.knowledge.add-content.sync-guru'),
        contentSource: 'sync-guru',
        onClick: this.syncGuru,
      },
    ];
  }

  get shouldShowConversationExtractionSection() {
    if (this.appService.app.canSeeSuggestions) {
      return false;
    }
    // If an app is SA then we only want to show this section if canUseExtractionExternalSources  is true, until we release it to all SA apps
    // source https://github.com/intercom/embercom/pull/94249/files#r1946430684
    return (
      !this.appService.app.canUseStandalone || this.appService.app.canUseExtractionExternalSources
    );
  }

  get excerptsEnabled() {
    return this.copilotApi.copilotSettings?.use_past_conversations ?? true;
  }

  @action
  createCustomAnswer() {
    this.knowledgeHubService.createContent(EntityType.Answer, {});
    this.trackAnalyticsEvent('create', 'custom_answer');
  }

  @action
  toggleCopilotOnlyAccordion() {
    this.openSectionIdForCopilotOnlyAccordion = this.openSectionIdForCopilotOnlyAccordion
      ? null
      : 'copilot-only-content-sources';
  }

  get hasSalesforceKnowledgeSource() {
    return this.knowledgeHubService.usageSummary?.[
      EntityType.FileSourceContent
    ]?.sync_sources?.some((source) => source.source_type === 'salesforce_knowledge');
  }

  private trackAnalyticsEvent(action: string, object?: string) {
    this.intercomEventService.trackAnalyticsEvent({
      tab: 'content',
      action,
      object,
      route: 'setup',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'FinAiAgent::Knowledge::AddContent': typeof AddContent;
  }
}
